/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, Tooltip, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { getYear } from '../../../../utils';
import { toFormatCurrency } from '../../../../../../utils/format';

const Q_REPORT = gql`
	query getEarlyWarningGroupingRiskRatioReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String) {
		getEarlyWarningGroupingRiskRatioReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

export default function RatioLifeGroupingRiskSpreadSheetTable({
	year = null,
	companyType = null,
	companyId = null,
	lifeData = null,
	groupingRiskDataExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);

	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState(null);

	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);

	const ratioCells = [
		{ cells: q1Cells, setCells: setQ1Cells, index: 0 },
		{ cells: q2Cells, setCells: setQ2Cells, index: 1 },
		{ cells: q3Cells, setCells: setQ3Cells, index: 2 },
	];

	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(() => {
		load();
	}, [year, companyType, companyId]);

	useEffect(() => {
		if (data?.getEarlyWarningGroupingRiskRatioReport) {
			setDetail(data?.getEarlyWarningGroupingRiskRatioReport);
		}
	}, [data]);

	useEffect(() => {
		if (detail && lifeData) {
			quarterDetail(detail);
		}
	}, [detail, lifeData]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.forEach(({ message }) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = {
			year: getYear(year),
			companyType,
			companyId,
		};
		loadReport({ variables });
	}

	function emptyCells(labels = [], i, lifeData = { q2Cells: [], q3Cells: [], q4Cells: [] }) {
		const getCells = (labelCode) => {
			switch (labelCode) {
				case 'GROPR':
					return [
						Array.isArray(lifeData?.q4Cells?.[1]) ? lifeData?.q4Cells[1] : [],
						Array.isArray(lifeData?.q4Cells?.[2]) ? lifeData?.q4Cells[2] : [],
						Array.isArray(lifeData?.q4Cells?.[3]) ? lifeData?.q4Cells[3] : [],
					];
				case 'GRLQR':
					return [
						Array.isArray(lifeData?.q4Cells?.[5]) ? lifeData?.q4Cells[5] : [],
						Array.isArray(lifeData?.q4Cells?.[6]) ? lifeData?.q4Cells[6] : [],
						Array.isArray(lifeData?.q4Cells?.[7]) ? lifeData?.q4Cells[7] : [],
						Array.isArray(lifeData?.q4Cells?.[8]) ? lifeData?.q4Cells[8] : [],
						Array.isArray(lifeData?.q4Cells?.[9]) ? lifeData?.q4Cells[9] : [],
					];
				case 'GRRALR':
					return [
						Array.isArray(lifeData?.q4Cells?.[11]) ? lifeData?.q4Cells[11] : [],
						Array.isArray(lifeData?.q4Cells?.[12]) ? lifeData?.q4Cells[12] : [],
						Array.isArray(lifeData?.q4Cells?.[13]) ? lifeData?.q4Cells[13] : [],
					];
				case 'GRPBR':
					return [
						Array.isArray(lifeData?.q4Cells?.[17]) ? lifeData?.q4Cells[17] : [],
						Array.isArray(lifeData?.q4Cells?.[18]) ? lifeData?.q4Cells[18] : [],
						Array.isArray(lifeData?.q4Cells?.[19]) ? lifeData?.q4Cells[19] : [],
					];
				default:
					return [];
			}
		};

		const calculateScore = (labelCode, cells, i) => {
			switch (labelCode) {
				case 'GROPR':
					const [opr1, opr2, opr3] = cells;
					const avgOR = (Number(opr1[0]?.value || 0) + Number(opr2[0]?.value || 0) + Number(opr3[0]?.value || 0)) / 3;
					switch (i) {
						case 0:
							return toFormatCurrency(avgOR, null);
						case 1:
							return 1;
						case 2:
							return toFormatCurrency(avgOR, null) * 1;
						default:
							return 0;
					}
				case 'GRLQR':
					const [lq1, lq2, lq3, lq4, lq5] = cells;
					const avgLQR =
						(Number(lq1[0]?.value || 0) +
							Number(lq2[0]?.value || 0) +
							Number(lq3[0]?.value || 0) +
							Number(lq4[0]?.value || 0) +
							Number(lq5[0]?.value || 0)) /
						5;
					switch (i) {
						case 0:
							return toFormatCurrency(avgLQR, null);
						case 1:
							return 1;
						case 2:
							return toFormatCurrency(avgLQR, null) * 1;
						default:
							return 0;
					}
				case 'GRRALR':
					const [lr1, lr2, lr3] = cells;
					const avgRL = (Number(lr1[0]?.value || 0) + Number(lr2[0]?.value || 0) + Number(lr3[0]?.value || 0)) / 3;
					switch (i) {
						case 0:
							return toFormatCurrency(avgRL, null);
						case 1:
							return 2;
						case 2:
							return toFormatCurrency(avgRL, null) * 2;
						default:
							return 0;
					}
				case 'GRPBR':
					const [pbr1, pbr2, pbr3] = cells;
					const avgPBR = (Number(pbr1[0]?.value || 0) + Number(pbr2[0]?.value || 0) + Number(pbr3[0]?.value || 0)) / 3;
					switch (i) {
						case 0:
							return toFormatCurrency(avgPBR, null);
						case 1:
							return 1;
						case 2:
							return toFormatCurrency(avgPBR, null) * 1;
						default:
							return 0;
					}
				default:
					return 0;
			}
		};

		return labels?.map((label) => {
			const cells = getCells(label?.code);
			const score = calculateScore(label?.code, cells, i);
			const truncated = Math.trunc(score * 100) / 100;

			return [
				{
					value: i === 0 || i === 1 ? toFormatCurrency(truncated, null) : toFormatCurrency(truncated, null),
					readOnly: true,
					style: {
						fontWeight: 'bold',
						textAlign: 'center',
					},
				},
			];
		});
	}

	function quarterCells({ labels = [], quarter = null }) {
		return labels?.map((label) => {
			const labelObject = quarter?.find((e) => e?.code === label?.code);
			return labelObject?.values || [{ value: '0', readOnly: true }];
		});
	}

	function quarterDetail(detailData) {
		if (!detailData || !detailData?.labels) return;

		const currentRiskScore = detailData?.q1?.length
			? quarterCells({ labels: detailData?.labels, quarter: detailData?.q1 })
			: emptyCells(detailData?.labels, 0, lifeData);

		const weight = detailData?.q2?.length
			? quarterCells({ labels: detailData?.labels, quarter: detailData?.q2 })
			: emptyCells(detailData?.labels, 1, lifeData);

		const riskScore = detailData?.q3?.length
			? quarterCells({ labels: detailData?.labels, quarter: detailData?.q3 })
			: emptyCells(detailData?.labels, 2, lifeData);

		const numRows = detailData?.labels?.length;

		const normalize = (cells) => (cells?.length === numRows ? cells : [...cells, ...Array(numRows - cells?.length).fill([{ value: '', readOnly: true }])]);

		setQ1Cells(normalize(currentRiskScore));
		setQ2Cells(normalize(weight));
		setQ3Cells(normalize(riskScore));

		groupingRiskDataExport({
			labels: detailData?.labels,
			q1Cells: currentRiskScore,
			q2Cells: weight,
			q3Cells: riskScore,
		});
	}

	function handleCellsChange(index, newData) {
		switch (index) {
			case 0:
				setQ1Cells(newData);
				break;
			case 1:
				setQ2Cells(newData);
				break;
			case 2:
				setQ3Cells(newData);
				break;
			default:
				break;
		}
	}

	const preventReadOnlyEdit = (oldData, newData) => {
		return newData.map((row, r) =>
			row.map((cell, c) => {
				if (oldData?.[r]?.[c]?.readOnly) {
					return oldData[r][c];
				}
				return cell;
			}),
		);
	};

	return (
		<Box>
			{loading && <Loading />}

			{!loading && !detail && (
				<Text color="gray.400" textAlign="center" mt={4}>
					{t('No Data')}
				</Text>
			)}

			{!loading && detail && (
				<Box className="ews-container">
					<div className="ews-1-row-head-ratio">{t('Grouping Risk Category')}</div>

					<Flex>
						{q1Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											<Tooltip label={t('Current Risk Score')}>
												<Text noOfLines={1}>{t('Current Risk Score')}</Text>
											</Tooltip>
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q2Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											<Tooltip label={t('Weight')}>
												<Text noOfLines={1}>{t('Weight')}</Text>
											</Tooltip>
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q3Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											<Tooltip label={t('Risk Score')}>
												<Text noOfLines={1}>{t('Risk Score')}</Text>
											</Tooltip>
										</td>
									</tr>
								</thead>
							</table>
						)}
					</Flex>

					<div>
						<EWSLabels type="EWS" labels={detail?.labels} active={active} customBackgroundColor="#f7fafc" />
						<Flex>
							{ratioCells?.map(({ cells, setCells, index }) => {
								return cells?.length > 0 ? (
									<Spreadsheet
										key={index}
										data={cells}
										onChange={(newData) => {
											const sanitized = preventReadOnlyEdit(cells, newData);

											handleCellsChange(index, sanitized);
										}}
										onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
										hideRowIndicators
										hideColumnIndicators
									/>
								) : null;
							})}
						</Flex>
					</div>
				</Box>
			)}
		</Box>
	);
}
